import {
    AbstractModel,
    Column,
    Model,
    FK,
    Mime,
    DataType,
    ElementDto,
    Data,
    Field,
    Enum,
    HttpMethod,
    Index,
} from "@clairejs/core";

import { User, GeoPlace } from "./user";
import { Service } from "./service";
import { ServiceProfile } from "./service-profile";
import { VendorReply } from "./vendor-reply";

export enum SearchStatus {
    SEARCHING = "searching", // waiting for replies
    CONFIRMED = "confirmed", // vendor selected
    CLOSED = "closed", // search closed after selected vendors
    CANCELLED = "cancelled", // search cancelled by vendor
}

export enum SearchCloseReason {
    user_requested = "user_requested",
    search_expired = "search_expired",
    no_vendor_found = "no_vendor_found",
}

@Data()
export class VendorInfo {
    @Field({
        isRequired: true,
    })
    dailyCounter!: number;

    @Field({
        ...FK({ model: ServiceProfile }),
        isRequired: true,
    })
    profileId!: string;

    @Field({
        ...FK({ model: User }),
        isRequired: true,
    })
    vendorId!: string;

    @Field({
        isRequired: true,
        floating: true,
    })
    distance!: number;

    @Field({
        description: "The rating of service profile",
        floating: true,
    })
    rating?: number;
}

@Model({
    ignoreCrud: [HttpMethod.POST],
})
@Index([["vendors.vendorId", 1]])
@Index([["replies.vendor", 1]])
@Index([["status", 1]])
@Index([["rejectedVendorIds", 1]])
@Index([["chosenVendors", 1]])
@Index([["cancelledVendors", 1]])
@Index([["userId", 1]])
@Index([["waiting", 1]])
@Index([["serviceId", 1]])
@Index([["createdAt", 1]])
@Index([["location", "2dsphere"]])
export class Search extends AbstractModel {
    @Column({
        isRequired: true,
        serverValue: true,
        ...FK({ model: User }),
    })
    userId!: string;

    @Column({
        isRequired: true,
        ...FK({ model: Service, cascade: "delete" }),
    })
    serviceId!: string;

    @Column({
        isRequired: true,
        description: "Name of the region",
        serverValue: true,
    })
    region!: string;

    @Field({
        isRequired: true,
        ...Enum(SearchStatus),
    })
    status!: SearchStatus;

    @Column({
        description: "Whether to auto pick the best vendor",
    })
    autoPick?: boolean;

    @Column({
        description: "The price proposed by the system when autoPick is on (in current currency)",
        floating: true,
    })
    autoPrice?: string;

    @Column({
        description: "When the search become available",
        isRequired: true,
        isTimestamp: true,
    })
    expiredAt!: number;

    @Column({
        description: "When the search is fulfilled",
        isTimestamp: true,
    })
    finishedAt?: number;

    @Column({
        description: "The serving location",
        isRequired: true,
    })
    location!: GeoPlace;

    @Column({
        description: "The destination location",
    })
    toLocation?: GeoPlace;

    @Column({
        description: "Ask currency",
        isRequired: true,
    })
    currency!: string;

    @Column({
        description: "Distance in meters to search for vendors",
        isRequired: true,
        rangeProps: { min: 0 },
    })
    searchRange!: number;

    @Column({
        vectorProps: { elementDataType: DataType.STRING },
        mimeProps: { public: true, type: Mime.IMAGE, maxFileKB: 1024 },
    })
    searchImages?: string[];

    @Column({
        description: "User's note for vendor",
        textLength: 512,
    })
    userNote?: string;

    @Column({
        description:
            "Whether to hide this search from active searches, useful when the search is partially served and user nolonger need it to be actively shown up",
    })
    hidden?: boolean;

    @Column({
        description: "The reason why the search is closed",
        dataType: DataType.STRING,
    })
    closeReason?: SearchCloseReason | string;

    @Column({
        description: "Replies of vendor",
        vectorProps: { elementDataType: DataType.OBJECT },
        serverValue: true,
        ...ElementDto(VendorReply),
    })
    replies: VendorReply[] = [];

    @Column({
        description: "All the vendors found with this search, from best rating to lowest rating",
        isRequired: true,
        serverValue: true,
        vectorProps: { elementDataType: DataType.OBJECT },
        ...ElementDto(VendorInfo),
    })
    vendors!: VendorInfo[];

    @Column({
        description: "Ids of rejected vendors",
        serverValue: true,
        vectorProps: { elementDataType: DataType.STRING },
        ...FK({ model: User }),
    })
    rejectedVendorIds?: string[];

    @Column({
        description: "Count of ids of vendors",
        serverValue: true,
        isRequired: true,
    })
    vendorCount!: number;

    @Field({
        isRequired: true,
        serverValue: true,
        description: "Amount of coin user had paid",
    })
    cost!: number;

    @Field({
        serverValue: true,
        description: "Amount of coin shared to vendor referer, key is vendorId, value is share amount",
    })
    referalShare?: Record<string, number>;

    @Field({
        serverValue: true,
        description: "Amount of coin shared to user referer",
    })
    referalUserShare?: number;

    @Field({
        description: "Select multiple vendors",
    })
    multiChoices?: boolean;

    @Field({
        description: "The chosen vendors",
        vectorProps: { elementDataType: DataType.STRING },
        ...FK({ model: User }),
    })
    chosenVendors?: string[];

    @Field({
        description: "The vendors that cancel the search after being confirmed",
        vectorProps: { elementDataType: DataType.STRING },
        ...FK({ model: User }),
    })
    cancelledVendors?: string[];

    @Column({
        description: "Whether this search is actively waiting for available vendors",
    })
    waiting?: boolean;

    @Column({
        description: "Whether refundBidSearch had been called",
    })
    refunded?: boolean;
}
